import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, RefreshCw } from 'lucide-react';

import type { ReportPreset, ReportRangeState } from './useReports';

interface ReportRangePickerProps {
  value: ReportRangeState;
  onChange: (next: ReportRangeState) => void;
  onFetch: () => void;
  busy?: boolean;
  /** Business dates the server resolved the instants to, echoed back to the user. */
  resolved?: { fromBusinessDate: string; toBusinessDate: string } | null;
  /** Applies a named range. Presets are hidden when absent. */
  onPreset?: (preset: ReportPreset) => void;
  /** Which preset the current From/To matches, if any. */
  activePreset?: ReportPreset | null;
}

const PRESETS: ReportPreset[] = ['today', 'yesterday', 'last7', 'thisMonth', 'allTime'];

/**
 * From / To pickers (SPEC §5.7 header) plus the Fetch Report button.
 *
 * These are date-TIME pickers on purpose: a venue reads its own day as
 * "25/7 09:00 -> 26/7 08:00", not as a calendar date. The instants are resolved
 * server-side to business dates, and the resolved dates are echoed back beneath
 * the controls — without that, an owner who picks 26/7 02:00 has no way to tell
 * that the figures they are looking at belong to the 25th.
 *
 * Each side is a BUTTON that folds a picker out and back (owner request): the
 * chosen instant reads as a labelled value at rest, and the editing control
 * only exists while it is being used.
 */
export function ReportRangePicker({
  value,
  onChange,
  onFetch,
  busy = false,
  resolved,
  onPreset,
  activePreset = null,
}: ReportRangePickerProps) {
  const { t } = useTranslation();

  return (
    <div>
      {/*
        Typing two datetimes to answer "how did last week go" is the kind of
        friction that makes a working filter feel broken — and "All time" is
        the only way to see the previous system's period totals, which are
        included whole or not at all.
      */}
      {onPreset ? (
        <div className="mb-3 flex flex-wrap gap-2">
          {PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              onClick={() => onPreset(preset)}
              aria-pressed={activePreset === preset}
              className={`rounded-full px-3 py-1.5 text-sm ${
                activePreset === preset
                  ? 'bg-primary text-primary-fg'
                  : 'border border-subtle bg-surface text-content-muted hover:text-content'
              }`}
            >
              {t(`reports.presets.${preset}`)}
            </button>
          ))}
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <DateTimeDropdown
          label={t('common.from')}
          value={value.from}
          onPick={(from) => onChange({ ...value, from })}
        />
        <DateTimeDropdown
          label={t('common.to')}
          value={value.to}
          onPick={(to) => onChange({ ...value, to })}
        />

        <button
          type="button"
          onClick={onFetch}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-fg hover:bg-primary-hover disabled:opacity-60"
        >
          <RefreshCw className={`h-4 w-4 ${busy ? 'animate-spin' : ''}`} aria-hidden />
          {t('reports.fetchReport')}
        </button>
      </div>

      {resolved ? (
        <p className="mt-2 text-xs text-content-muted">
          {t('reports.businessDayNote', {
            from: resolved.fromBusinessDate,
            to: resolved.toBusinessDate,
          })}
        </p>
      ) : null}
    </div>
  );
}

/**
 * One side of the range: a labelled value that reads like a chip, with the
 * actual datetime input folded away underneath.
 *
 * FOLDING RULES, because the two input worlds commit differently:
 * - A COARSE pointer (a phone) opens its native wheel, which commits exactly
 *   once — so the first change folds the dropdown straight back up, which is
 *   the "after picking it will go back up" the owner asked for.
 * - A FINE pointer types segment by segment and fires a change per segment —
 *   folding on the first one would eat the keyboard mid-edit, so the desktop
 *   closes on outside click, Escape, or pressing the button again.
 */
function DateTimeDropdown({
  label,
  value,
  onPick,
}: {
  label: string;
  value: string;
  onPick: (next: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (event: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    // Jump straight into the native picker while the tap's user-activation
    // window is still warm — one press opens the wheel, not a second target.
    try {
      inputRef.current?.showPicker?.();
    } catch {
      // Some engines refuse outside a direct handler; the focused input with
      // its own affordance is a perfectly fine fallback.
      inputRef.current?.focus();
    }
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        className={`flex items-center gap-2.5 rounded-control border bg-input px-3 py-1.5 text-start transition-colors ${
          open ? 'border-primary' : 'border-subtle hover:border-strong'
        }`}
      >
        <span>
          <span className="block text-[10px] uppercase tracking-[0.22em] text-content-muted">
            {label}
          </span>
          <span className="tabular block text-sm leading-tight text-content">
            {value ? value.replace('T', ' ') : '—'}
          </span>
        </span>
        <ChevronDown
          className={`h-3.5 w-3.5 shrink-0 text-content-secondary transition-transform ${
            open ? 'rotate-180' : ''
          }`}
          aria-hidden
        />
      </button>

      {open ? (
        <div
          className="absolute z-30 mt-1 rounded-card border border-subtle bg-overlay p-2 shadow-2xl"
          style={{ insetInlineStart: 0 }}
        >
          <input
            ref={inputRef}
            type="datetime-local"
            value={value}
            onChange={(event) => {
              onPick(event.target.value);
              if (window.matchMedia('(pointer: coarse)').matches) setOpen(false);
            }}
            aria-label={label}
            className="tabular rounded-control border border-subtle bg-input px-2.5 py-1.5 text-sm text-content outline-none focus:border-primary"
          />
        </div>
      ) : null}
    </div>
  );
}
